import React, { Fragment, useState, useEffect } from "react";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import ResourceChangesTable from "../../components/ResourceChangesTable.jsx";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useQuery } from "@tanstack/react-query";
import { useKeycloak } from "@react-keycloak/web";
import { useFilterStoreWithMomentDates } from "../../store/filterStore.js";
import { LineChartComponent } from "../../components/chart-components/LineChartComponent.jsx";
const API_URL = process.env.REACT_APP_API_URL;

const ResourceChanges = () => {
	const [openRows] = useState([]);
	const [resourceList, setResourceList] = useState([]);
	const [resourceChangesAggregated, setResourceChangesAggregated] = useState([]);
	const [resourceKPIs, setResourceKPIs] = useState({
		Compute: 0,
		Storage: 0,
		Database: 0,
		Networking: 0,
	});
	const { keycloak } = useKeycloak();

	// Load data from sessionStorage on component mount
	useEffect(() => {
		const savedResourceList = sessionStorage.getItem('resourceChangesList');
		const savedResourceChangesAggregated = sessionStorage.getItem('resourceChangesAggregated');
		const savedResourceKPIs = sessionStorage.getItem('resourceKPIs');

		if (savedResourceList) {
			try {
				setResourceList(JSON.parse(savedResourceList));
			} catch (e) {
				console.warn('Failed to parse saved resource list:', e);
			}
		}

		if (savedResourceChangesAggregated) {
			try {
				setResourceChangesAggregated(JSON.parse(savedResourceChangesAggregated));
			} catch (e) {
				console.warn('Failed to parse saved resource changes aggregated:', e);
			}
		}

		if (savedResourceKPIs) {
			try {
				setResourceKPIs(JSON.parse(savedResourceKPIs));
			} catch (e) {
				console.warn('Failed to parse saved resource KPIs:', e);
			}
		}
	}, []);

	// Save data to sessionStorage when it changes
	useEffect(() => {
		if (resourceList && resourceList.length > 0) {
			sessionStorage.setItem('resourceChangesList', JSON.stringify(resourceList));
		}
	}, [resourceList]);

	useEffect(() => {
		if (resourceChangesAggregated && resourceChangesAggregated.length > 0) {
			sessionStorage.setItem('resourceChangesAggregated', JSON.stringify(resourceChangesAggregated));
		}
	}, [resourceChangesAggregated]);

	useEffect(() => {
		if (resourceKPIs && Object.keys(resourceKPIs).length > 0) {
			sessionStorage.setItem('resourceKPIs', JSON.stringify(resourceKPIs));
		}
	}, [resourceKPIs]);

	// Use Zustand store for date state with moment dates
	const { startDate, endDate } = useFilterStoreWithMomentDates();

	function toggleRow(resourceType) {
		const openResourceTypes = sessionStorage.getItem("openResourceTypes");

		let openRows = openResourceTypes ? openResourceTypes.split(",") : [];

		const index = openRows.indexOf(String(resourceType));

		if (index > -1) {
			openRows = openRows.filter((row) => row !== String(resourceType));
		} else {
			openRows = [...openRows, String(resourceType)];
		}

		sessionStorage.setItem("openResourceTypes", openRows.join(","));
	}

	function buildRequest() {
		let reqFilters = [];

		return {
			filters: reqFilters,
			start_date: startDate.format("YYYY-MM-DD"),
			end_date: endDate.format("YYYY-MM-DD"),
		};
	}

	function getRequestOptions() {
		if (keycloak.token === "") return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + keycloak.token);

		let method = "POST";

		const requestBody = buildRequest();

		let requestOptions = {
			body: JSON.stringify(requestBody),
			method: method,
			headers: headers,
		};

		return requestOptions;
	}

	const retrieveResourceChangesAggregated = async(token) => {
		if (token === "") return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + token)
		const response = await fetch(API_URL + "/get-resource-changes-aggregated", getRequestOptions());

		if (!response.ok) {
			throw new Error("Failed to fetch data");
		}

		return response.json();
	};

	useQuery(
		["resourceChangesAggregated", startDate, endDate],
		() => retrieveResourceChangesAggregated(keycloak.token),
		{
			onSuccess: (data) => {
				setResourceChangesAggregated(data.ResourceChanges);
			},
			enabled: keycloak.token !== "",
			staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
			refetchOnWindowFocus: false, // Prevent refetching when window regains focus
			cacheTime: 10 * 60 * 1000, // Keep data in cache for 10 minutes
			refetchOnMount: false, // Prevent refetching when component mounts if data is fresh
		}
	);	


	const retrieveResourceChanges = async(token) => {
		if (token === "" || startDate === null || endDate === null) return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + token)
		const response = await fetch(API_URL + "/get-resource-changes", getRequestOptions());

		if (!response.ok) {
			throw new Error("Failed to fetch data");
		}

		return response.json();
	};

	useQuery(
		["resourceChanges", startDate, endDate],
		() => retrieveResourceChanges(keycloak.token),
		{
			onSuccess: (data) => {
				setResourceList(data.ResourceChanges);
			},
			enabled: keycloak.token !== "",
			staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
			refetchOnWindowFocus: false, // Prevent refetching when window regains focus
			cacheTime: 10 * 60 * 1000, // Keep data in cache for 10 minutes
			refetchOnMount: false, // Prevent refetching when component mounts if data is fresh
		}
	);	


	const retrieveResourceKPIs = async(token) => {
		if (token === "") return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + token)

		let method = "POST";

		const requestBody = buildRequest();

		let requestOptions = {
			body: JSON.stringify(requestBody),
			method: method,
			headers: headers,
		};

		const response = await fetch(API_URL + "/get-resource-kpis", requestOptions);

		if (!response.ok) {
			throw new Error("Failed to fetch data");
		}

		return response.json();
	}

	useQuery(
		["resourceKPIs"],
		() => retrieveResourceKPIs(keycloak.token),
		{
			onSuccess: (data) => {
				const kpis = {};
				let total = 0;
				let totalUntagged = 0;

				data.ResourceKPIs.forEach((kpi) => {
					kpis["PercentageTagged" + kpi.resource_cluster] = ((kpi.total_resources - kpi.untagged_resources) / kpi.total_resources) * 100;
					total += kpi.total_resources;
					totalUntagged += kpi.untagged_resources;
				});

				kpis.TotalResources = total;
				kpis.TotalUntaggedResources = totalUntagged;
				kpis.PercentageTaggedTotal = ((total - totalUntagged) / total) * 100;

				setResourceKPIs(kpis);
			},
			enabled: keycloak.token !== "",
			staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
			refetchOnWindowFocus: false, // Prevent refetching when window regains focus
			cacheTime: 10 * 60 * 1000, // Keep data in cache for 10 minutes
			refetchOnMount: false, // Prevent refetching when component mounts if data is fresh
		}
	);


	return (
		<Fragment>
			<AdministrationHeader
				title="Resources"
				subTitle="Define and manage the tags that are used in the different cloud environments"
				className="text-text-3"
				icon={icon({name: "layer-group", style: "light"})}
			/>

			{resourceChangesAggregated && resourceChangesAggregated.length > 0 && (
				<div className="text-left flex flex-auto flex-col ml-10 mt-4 border-solid border-border">
					<div className="flex flex-row">
						{/* <KPI
							title="Total Resources"
							currentValue={resourceKPIs.TotalUntaggedResources}
							// previousValue={totalUnits}
							format="decimal"
							// showChange={true}
							style="small"
							backgroundColor={"teal9"}
						/>	 */}

						{/* <KPI
							title="Tagged Compute Resources"
							currentValue={resourceKPIs.PercentageTaggedCompute}
							// previousValue={resourceKPIs.filter(kpi => kpi.resource_group === "Compute").untagged_resources}
							format="percentage"
							// showChange={true}
							style="small"
							backgroundColor={"lightblue10"}
						/>

						<KPI
							title="Tagged Storage Resources"
							currentValue={resourceKPIs.PercentageTaggedStorage}
							// previousValue={resourceKPIs.filter(kpi => kpi.resource_group === "Storage").untagged_resources}
							format="percentage"
							// showChange={true}
							style="small"
							backgroundColor={"pink10"}
						/>

						<KPI
							title="Tagged Database Resources"
							currentValue={resourceKPIs.PercentageTaggedDatabase}
							// previousValue={resourceKPIs.filter(kpi => kpi.resource_group === "Database").untagged_resources}
							format="percentage"
							// showChange={true}
							style="small"
							backgroundColor={"yellow10"}
						/>

						<KPI
							title="Tagged Networking Resources"
							currentValue={resourceKPIs.PercentageTaggedNetworking}
							// previousValue={resourceKPIs.filter(kpi => kpi.resource_group === "Networking").untagged_resources}
							format="percentage"
							// showChange={true}
							style="small"
							backgroundColor={"coolgrey9"}
						/> */}
					</div>

					<div className="pt-0 pb-2 my-2 rounded-lg h-screen w-screen flex flex-auto flex-col">
						<LineChartComponent
							data={resourceChangesAggregated}
							title="Resource changes"
						/>

						<ResourceChangesTable
							data={resourceList}
							toggleRow={toggleRow}
							openRows={openRows}
						/>
					</div>
				</div>
			)}
		</Fragment>
	);
};

export default ResourceChanges;
